// Tasarim seti listesi (onayli setten): 1) her tasarim ayri uretilir, 2) kendi tisort
// rengindeki mockup'a basilir, 3) grup gorseli (ilham fotografindaki tisortlere bizim
// tasarimlar), 4) taslak: renk ekseni yerine alicinin sectigi TASARIM ekseni x Beden.

import { useEffect, useState } from "react";
import { useShopVertical } from "../../lib/useShopVertical";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ArrowRight, CheckCircle2, Image as ImageIcon, Link2, Loader2, Printer, RotateCcw, Sparkles, Trash2, Unlink } from "lucide-react";
import { useDesignLinks } from "./FrontBackPanel";
import { garmentHex } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";
import { fidelityOptionsFor, type Fidelity } from "../../lib/designPrompt";
import type { DesignSet } from "./DesignSetPanel";

const abs = (u?: string) => (!u ? "" : /^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u.startsWith("/") ? "" : "/"}${u}`);
async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, {
    method, headers: { ...authHeaders(), "Content-Type": "application/json" }, ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error ?? `İstek başarısız (${r.status})`);
  return j.data as T;
}
type Cand = { id: string; label: string; imageUrl: string; color: string; exact: boolean };
const btn = "inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50";

export function DesignSetListing({ analysisId, set, onChanged }: { analysisId: string; set: DesignSet; onChanged: () => void }) {
  const { models, model, setModel } = useDesignModel("erc:model:design-set");
  // IP sIkI modlu magazada Yakin/Benzer yok, varsayilan Dengeli
  const ipStrict = !!useShopVertical().shop?.ipStrict;
  const FIDELITY_OPTIONS = fidelityOptionsFor(ipStrict);
  const [fidelity, setFidelity] = useState<Fidelity>(ipStrict ? "balanced" : "near");
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [tplId, setTplId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const items = set.items;
  const designsReady = items.filter((it) => it.design?.status === "ready").length;
  const running = items.some((it) => it.design?.status === "running") || set.collage?.status === "running";

  // Arka plan isleri surerken tazele
  useEffect(() => {
    if (!running) return;
    const t = setInterval(onChanged, 5000);
    return () => clearInterval(t);
  }, [running]);

  const { data: cands } = useQuery({
    queryKey: ["design-set-cands", analysisId, items.map((i) => `${i.key}:${i.color}`).join("|")],
    queryFn: () => call<Record<string, Cand[]>>("GET", `/${analysisId}/design-set/mockup-candidates`),
  });
  // Varsayilan mockup: once basilmis olan, yoksa en yakin aday
  useEffect(() => {
    if (!cands) return;
    setChoice((c) => Object.fromEntries(items.map((it) => [it.key, c[it.key] ?? it.mockup?.mockupId ?? cands[it.key]?.[0]?.id ?? ""])));
  }, [cands]);
  const { data: templates = [] } = useQuery({
    queryKey: ["listing-templates"],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() });
      return ((await r.json().catch(() => ({}))).data ?? []) as Array<{ id: string; name: string; platform?: string }>;
    },
  });

  const onErr = (e: unknown) => setErr((e as Error).message);
  const gen = useMutation({ mutationFn: (keys?: string[]) => call("POST", `/${analysisId}/design-set/generate`, { model, fidelity, ...(keys ? { keys } : {}) }), onSuccess: () => { setErr(null); onChanged(); }, onError: onErr });
  // Hazir (atolyede uretilmis) tasarimi kaleme bagla — uretmeden
  const { data: links = [] } = useDesignLinks(analysisId);
  const [linkFor, setLinkFor] = useState<string | null>(null);
  const qc = useQueryClient();
  const attach = useMutation({
    mutationFn: ({ key, id, pool }: { key: string; id: string; pool?: boolean }) =>
      call("POST", `/${analysisId}/design-set/items/${key}/use-design`, pool ? { poolDesignId: id } : { analysisDesignId: id }),
    onSuccess: () => { setErr(null); setLinkFor(null); void qc.invalidateQueries({ queryKey: ["design-links", analysisId] }); onChanged(); },
    onError: onErr,
  });
  const usedIds = new Set(items.map((it) => it.design?.analysisDesignId).filter(Boolean));
  const linkable = links.filter((l) => !usedIds.has(l.analysisDesignId) && !l.backOf);
  const del = useMutation({
    mutationFn: (key: string) => call("DELETE", `/${analysisId}/design-set/items/${key}/design`),
    onSuccess: () => { setErr(null); onChanged(); }, onError: onErr,
  });
  const print = useMutation({ mutationFn: () => call<{ printed: number; failed: number }>("POST", `/${analysisId}/design-set/mockups`, { choice }), onSuccess: (r) => { setErr(r.failed ? `${r.failed} mockup basılamadı (baskı alanı tanımlı mı?)` : null); onChanged(); }, onError: onErr });
  const collage = useMutation({ mutationFn: () => call("POST", `/${analysisId}/design-set/collage`, { model }), onSuccess: () => { setErr(null); onChanged(); }, onError: onErr });
  const draft = useMutation({
    mutationFn: (force: boolean) => call<{ draftId: string; unknownColors?: string[] }>("POST", `/${analysisId}/design-set/draft`, { listingTemplateId: tplId, force }),
    onSuccess: (r) => {
      setErr(r.unknownColors?.length ? `Taslak açıldı. Şu renkler liste şablonunun renklerinde yok, bu tasarımların bütün bedenleri açık kaldı: ${r.unknownColors.join(", ")}. Renk adını şablondakiyle aynı yaz ya da bedenleri Listing Builder'da kontrol et.` : null);
      onChanged();
    },
    onError: onErr,
  });

  return (
    <div className="space-y-4 rounded-lg border border-indigo-100 bg-indigo-50/30 p-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Tasarım seti listesi</p>
        <p className="text-xs text-gray-600">Her tasarım ayrı üretilir ya da bu analizde zaten ürettiğin tasarım kaleme bağlanır ("Hazır tasarımı bağla"), sonra kendi tişört rengindeki mockup'a basılır. Taslakta renk menüsü yerine alıcının seçtiği "{set.variationName}" menüsü olur ({items.length} seçenek × beden); fiyat bedenden gelir.</p>
      </div>

      {/* 1. Tasarimlar */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-700">1. Tasarımlar ({designsReady}/{items.length})</p>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Görsel modeli" value={model} onChange={(e) => setModel(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
            {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
          </select>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="İlhama yakınlık">
            {FIDELITY_OPTIONS.filter((f) => f.key !== "original").map((f) => (
              <button key={f.key} onClick={() => setFidelity(f.key)} aria-pressed={fidelity === f.key} title={f.hint}
                className={clsx("rounded-md px-2 py-1 text-xs", fidelity === f.key ? "bg-indigo-50 font-medium text-indigo-700" : "text-gray-600 hover:bg-gray-50")}>{f.label}</button>
            ))}
          </div>
          <button onClick={() => gen.mutate(undefined)} disabled={!model || gen.isPending || designsReady === items.length}
            className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}>
            {gen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {designsReady ? `Eksikleri üret (${items.length - designsReady})` : `Tasarımları üret (${items.length})`}
          </button>
          <span className="text-xs text-gray-500">tasarım başına ~1 dk, 3'er 3'er</span>
          {items.some((it) => it.design && it.design.status !== "running") && (
            <button onClick={async () => {
              const keys = items.filter((it) => it.design && it.design.status !== "running").map((it) => it.key);
              if (!window.confirm(`${keys.length} tasarım ve mockup'ları silinsin mi? Tasarımlar sayfasından da kalkar.`)) return;
              for (const k of keys) await del.mutateAsync(k).catch(() => undefined);
            }} disabled={del.isPending} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600">
              {del.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Tüm tasarımları sil
            </button>
          )}
        </div>
      </div>

      {/* Tasarim kartlari: tasarim + secilen renk mockup'i */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((it) => {
          const st = it.design?.status;
          return (
            <div key={it.key} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="relative aspect-square" style={{ background: it.hex ?? garmentHex(it.color) }}>
                {it.mockup ? <img src={abs(it.mockup.url)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  : it.design?.url ? <img src={abs(it.design.url)} alt="" loading="lazy" className="h-full w-full object-contain p-2" />
                  : <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">{st === "running" ? <Loader2 className="h-5 w-5 animate-spin" /> : st === "failed" ? "Başarısız" : "Bekliyor"}</span>}
                {it.mockup && it.design?.url && <img src={abs(it.design.url)} alt="" className="absolute bottom-1 right-1 h-10 w-10 rounded border border-white/70 bg-white/60 object-contain" />}
              </div>
              <div className="space-y-1 p-1.5">
                <p className="truncate text-xs font-medium text-gray-900" title={it.title}>{it.title}</p>
                <p className="text-[11px] text-gray-500">{it.color}{it.design?.sku ? ` · ${it.design.sku}` : ""}</p>
                {st === "failed" && <p className="truncate text-[11px] text-red-600" title={it.design?.error}>{it.design?.error}</p>}
                {st === "ready" && (
                  <>
                    <select aria-label={`${it.title} mockup`} value={choice[it.key] ?? ""} onChange={(e) => setChoice((c) => ({ ...c, [it.key]: e.target.value }))}
                      className="w-full rounded border border-gray-200 px-1 py-0.5 text-[11px]">
                      <option value="">Mockup seç…</option>
                      {(cands?.[it.key] ?? []).map((c) => <option key={c.id} value={c.id}>{c.exact ? "✓ " : ""}{c.color} · {c.label}</option>)}
                    </select>
                    <button onClick={() => gen.mutate([it.key])} disabled={gen.isPending} className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-indigo-600"><RotateCcw className="h-3 w-3" /> Yeniden üret</button>
                  </>
                )}
                {(st === "ready" || st === "failed") && (
                  it.design?.linked ? (
                    <button onClick={() => del.mutate(it.key)} disabled={del.isPending} title="Hazır tasarım silinmez, yalnız bu kalemden ayrılır"
                      className="ml-2 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-600"><Unlink className="h-3 w-3" /> Bağı kaldır</button>
                  ) : (
                    <button onClick={() => { if (window.confirm(`"${it.title}" tasarımı ve mockup'ı silinsin mi? Tasarımlar sayfasından da kalkar.`)) del.mutate(it.key); }}
                      disabled={del.isPending} className="ml-2 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-600"><Trash2 className="h-3 w-3" /> Sil</button>
                  )
                )}
                {st !== "running" && !it.design?.linked && (
                  <button onClick={() => setLinkFor(linkFor === it.key ? null : it.key)} className="flex items-center gap-1 text-[11px] text-indigo-600 hover:underline">
                    <Link2 className="h-3 w-3" /> Hazır tasarımı bağla
                  </button>
                )}
                {it.design?.linked && <span className="inline-block rounded bg-indigo-50 px-1 text-[10px] font-medium text-indigo-700">hazır tasarım</span>}
                {linkFor === it.key && (
                  <div className="space-y-1 rounded border border-indigo-100 bg-indigo-50/50 p-1">
                    {linkable.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {linkable.map((l) => (
                          <button key={l.analysisDesignId} onClick={() => attach.mutate({ key: it.key, id: l.analysisDesignId })} disabled={attach.isPending} title={l.sku ?? ""}
                            className="h-10 w-10 overflow-hidden rounded border border-gray-200 bg-white hover:border-indigo-400">
                            <img src={l.url} alt="" loading="lazy" className="h-full w-full object-contain" />
                          </button>
                        ))}
                      </div>
                    )}
                    <OtherDesigns onPick={(id) => attach.mutate({ key: it.key, id, pool: true })} busy={attach.isPending} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {cands && items.some((it) => !(cands[it.key] ?? []).some((c) => c.exact)) && (
        <p className="text-xs text-amber-700">Bazı renkler için tam eşleşen mockup yok (✓ olmayanlar en yakın renk). Mockup Yönetimi'nde o renkte tek ürünlü mockup ekleyip AI ile analiz et.</p>
      )}

      {/* 2-3. Gorseller */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-700">2. Görseller</span>
        <button onClick={() => print.mutate()} disabled={print.isPending || designsReady === 0}
          className={`${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
          {print.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Renk mockup'larına bas
        </button>
        <button onClick={() => collage.mutate()} disabled={collage.isPending || set.collage?.status === "running" || designsReady < items.length}
          title="İlham fotoğrafındaki tişörtlerin baskısız haline tasarımlar sırayla basılır (sıra tablodaki sıra)"
          className={`${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
          {collage.isPending || set.collage?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} {set.collage?.status === "ready" ? "Grup görselini yeniden bas" : "Grup görseli"}
        </button>
      </div>
      {set.collage?.status === "failed" && <p className="text-xs text-red-600">{set.collage.error ?? "Grup görseli kurulamadı."}</p>}
      {set.collage?.url && (
        <a href={abs(set.collage.url)} target="_blank" rel="noopener noreferrer" title="Tam boyutta aç">
          <img src={abs(set.collage.url)} alt="Grup görseli" className="max-h-80 rounded-lg border border-gray-200 object-contain" />
        </a>
      )}

      {/* 4. Taslak */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-700">3. Taslak</span>
        <select aria-label="Liste şablonu" value={tplId} onChange={(e) => setTplId(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="">Liste şablonu seç…</option>
          {templates.filter((t) => !t.platform || t.platform === "etsy").map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button onClick={() => draft.mutate(Boolean(set.draftId))} disabled={!tplId || draft.isPending || designsReady < items.length}
          className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>
          {draft.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {set.draftId ? "Yeniden taslağa çevir" : "Taslağa çevir"}
        </button>
        {set.draftId && (
          <Link to={`/listing-builder?id=${set.draftId}`} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">Taslağı aç <ArrowRight className="h-4 w-4" /></Link>
        )}
      </div>
      <p className="text-xs text-gray-500">Etsy'ye gönderilmez; taslak Listing Builder'da kontrol edilip yayınlanır. Siparişte ürün SKU'su tasarımın SKU'su ile başlar, sipariş ekranı doğru tasarımı gösterir.</p>
      {err && <p className="text-sm text-red-600">{err}</p>}
    </div>
  );
}

/** Baska analizlerden tasarim: ayni nisin farkli ilham listelerinden uretilen tasarimlar tek sete */
function OtherDesigns({ onPick, busy }: { onPick: (poolDesignId: string) => void; busy: boolean }) {
  const [q, setQ] = useState("");
  const { data = [], isFetching } = useQuery({
    queryKey: ["design-search", q], enabled: q.trim().length >= 2,
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/designs?search=${encodeURIComponent(q.trim())}&noListing=1&limit=24`, { headers: authHeaders() });
      return ((await r.json().catch(() => ({}))).data ?? []) as Array<{ id: string; sku: string | null; name: string; thumbnailUrl: string | null; editedUrl: string | null; originalUrl: string }>;
    },
  });
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Diğer analizler: SKU ya da ad ara" aria-label="Diğer analizlerdeki tasarımları ara"
        className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-[11px]" />
      {isFetching && <Loader2 className="mt-1 h-3 w-3 animate-spin text-gray-400" />}
      {data.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {data.map((d) => (
            <button key={d.id} onClick={() => onPick(d.id)} disabled={busy} title={d.sku || d.name}
              className="h-10 w-10 overflow-hidden rounded border border-gray-200 bg-white hover:border-indigo-400">
              <img src={abs(d.thumbnailUrl || d.editedUrl || d.originalUrl)} alt="" loading="lazy" className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
